import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { usePathname, useRouter } from 'expo-router';
import Banner from '../ui/Banner';
import colors from '../../lib/colors';
import { interRegular, interSemiBold } from '../../lib/fonts';
import { useFontLoading } from '../../lib/FontContext';
import { shouldPromptBillingPreviewCheckout } from '../../lib/entitlements';
import { useCurrentEntitlements } from '../../lib/hooks/useCurrentEntitlements';
import {
    buildBillingPreviewTrialHref,
    getBillingPreviewState,
} from '../../lib/billingPreviewState';
import { canShowPreviewTrialBanner } from '../../lib/previewTrialBannerVisibility';
import { Trans } from '@lingui/react/macro';

export { canShowPreviewTrialBanner } from '../../lib/previewTrialBannerVisibility';

export default function PreviewTrialBanner() {
    const pathname = usePathname();
    const router = useRouter();
    const { fontsLoaded } = useFontLoading();
    const shouldLoad = canShowPreviewTrialBanner(pathname);
    const entitlementsQuery = useCurrentEntitlements({ enabled: shouldLoad });
    const shouldShow = shouldLoad && shouldPromptBillingPreviewCheckout(entitlementsQuery.data);
    const previewState = getBillingPreviewState(entitlementsQuery.data);
    const trialHref = buildBillingPreviewTrialHref({
        projectId: previewState.projectId,
        resultDigestId: previewState.resultDigestId,
    });

    if (!shouldShow) {
        return null;
    }

    return (
        <View style={styles.shell}>
            <View style={styles.inner}>
                <Banner type="info" variant="subtle" style={styles.banner}>
                    <Text style={[styles.message, fontsLoaded && interRegular]}><Trans id="billing.previewtrialbanner.trackingIsPausedAfterYourOneTimePreviewStartA14Day" comment="UI copy in components/billing/PreviewTrialBanner.tsx">
                        Tracking is paused after your one-time preview.{' '}
                        <Text
                            style={[styles.link, fontsLoaded && interSemiBold]}
                            onPress={() => router.push(trialHref)}
                            accessibilityRole="link"
                        >
                            Start a 14-day free trial to keep checking for new papers
                        </Text>
                        .
                    </Trans></Text>
                    <Text style={[styles.smallPrint, fontsLoaded && interRegular]}><Trans id="billing.previewtrialbanner.noChargeTodayCancelBeforeTheTrialEndsAndPay" comment="UI copy in components/billing/PreviewTrialBanner.tsx">
                        No charge today. Cancel before the trial ends and pay nothing.
                    </Trans></Text>
                </Banner>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    shell: {
        backgroundColor: colors.bgWeak,
        paddingHorizontal: 20,
        paddingTop: 16,
        paddingBottom: 16,
    },
    inner: {
        width: '100%',
        maxWidth: 1200,
        alignSelf: 'center',
    },
    banner: {
        marginTop: 0,
        marginBottom: 0,
        marginHorizontal: 0,
    },
    message: {
        color: colors.text900,
        fontSize: 15,
        lineHeight: 22,
    },
    link: {
        color: colors.primaryGreenDark,
        textDecorationLine: 'underline',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
        }),
    },
    smallPrint: {
        color: colors.text500,
        fontSize: 13,
        lineHeight: 19,
        marginTop: 4,
    },
});
